import { clamp } from '@/math/angles';

/**
 * Loop.ts — fixed-timestep game loop with render interpolation.
 *
 * Why fixed timestep? Physics that advances in constant-size steps feels the
 * same on a 60Hz phone and a 144Hz monitor, stays deterministic (vital for our
 * seeded tracks) and is easy to unit-test. The renderer still draws as fast as
 * the device allows; we interpolate between the last two physics states by a
 * fraction `alpha` so motion looks smooth between steps.
 */

export interface StepPlan {
  /** How many fixed physics steps to run this frame. */
  steps: number;
  /** Leftover time carried into the next frame. */
  accumulator: number;
  /** Render blend factor in [0,1): how far we are toward the next step. */
  alpha: number;
  /** Whole steps DISCARDED by the anti-spiral cap this frame (0 normally).
   *  Online, the driver re-simulates these itself (local stall compensation,
   *  see WsDriver.absorbDroppedTicks) instead of falling behind the server. */
  dropped: number;
}

/**
 * Pure accumulator math (no timers), so it can be unit-tested. Given the
 * carried-over accumulator and this frame's delta, decide how many fixed steps
 * of size `fixedDt` to run, clamped to `maxSteps`.
 */
export function planSteps(
  accumulator: number,
  frameDt: number,
  fixedDt: number,
  maxSteps: number,
): StepPlan {
  let acc = accumulator + frameDt;
  let steps = 0;
  while (acc >= fixedDt && steps < maxSteps) {
    acc -= fixedDt;
    steps++;
  }
  // If we hit the step cap, drop the backlog so we never "spiral" (each slow
  // frame queuing more work than the next frame can clear).
  let dropped = 0;
  if (steps >= maxSteps) {
    dropped = Math.floor(acc / fixedDt);
    acc = acc % fixedDt;
  }
  const alpha = clamp(acc / fixedDt, 0, 1);
  return { steps, accumulator: acc, alpha, dropped };
}

/**
 * Whole physics ticks of real time a frame LOST to the frame clamp (M143).
 *
 * `maxFrameMs` stops one giant frame (a frozen tab, a long main-thread stall)
 * from dumping a huge backlog of steps on the loop — but the time beyond the
 * clamp used to vanish without a trace. Offline that is the right outcome (the
 * world simply paused). ONLINE it is not: the server never pauses, so every
 * vanished tick left the client's clock that far behind its lead, to be found
 * out a round trip later from late-input reports and nudged back at 15
 * ticks/s (a 0.4 s freeze cost ~1.2 s of late controls — measured). Reported
 * with the cap's own drops, the online driver puts its clock right at once.
 */
export function lostTicks(rawFrameMs: number, maxFrameMs: number, fixedDt: number): number {
  if (!(rawFrameMs > maxFrameMs)) return 0;
  return Math.floor((rawFrameMs - maxFrameMs) / 1000 / fixedDt);
}

export interface LoopOptions {
  fixedDt: number; // seconds per physics step
  maxFrameMs: number; // clamp giant frame gaps
  maxStepsPerFrame: number;
  update: (dt: number) => void; // advance physics by one fixed step
  render: (alpha: number) => void; // draw, interpolated by alpha
  /** `n` whole steps of real time were NOT simulated this frame: discarded by
   *  the anti-spiral cap and/or lost beyond the frame clamp (a stall). */
  onDropped?: (n: number) => void;
  /** Loading round: the render-rate cap skipped this frame's draw. */
  onRenderSkipped?: () => void;
}

export class Loop {
  private opts: LoopOptions;
  private running = false;
  private rafId = 0;
  private last = 0;
  private accumulator = 0;

  constructor(opts: LoopOptions) {
    this.opts = opts;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** M111c: minimum interval between DRAWS (ms); 0 = draw every rAF. Physics
   *  steps are untouched — this only skips render calls. ProMotion iPhones
   *  drive WKWebView's rAF at 120 Hz, doubling GPU heat for imperceptible
   *  gain (the TestFlight "phones get hot" report) — a 60 Hz cap halves it.
   *  The 1.5 ms slack makes a 120 Hz stream land cleanly on alternate frames
   *  while a true 60 Hz stream never skips a draw. */
  private minRenderIntervalMs = 0;
  private lastRenderAt = 0;

  setMaxRenderHz(hz: number | null): void {
    this.minRenderIntervalMs = hz && hz > 0 ? 1000 / hz - 1.5 : 0;
  }

  private frame = (now: number): void => {
    if (!this.running) return;

    // Schedule the next frame FIRST: if update/render throws, the exception
    // still surfaces in the console but it can never silently kill the loop
    // and freeze the game on its last frame (that exact failure stranded a
    // tour transition once — never again).
    this.rafId = requestAnimationFrame(this.frame);

    // Clamp the frame delta so a long stall (tab backgrounded, breakpoint)
    // can't dump a huge backlog of physics steps on us all at once.
    const rawMs = now - this.last;
    const frameMs = Math.min(rawMs, this.opts.maxFrameMs);
    this.last = now;
    // Real time the clamp is about to discard (M143) — see lostTicks().
    const lost = lostTicks(rawMs, this.opts.maxFrameMs, this.opts.fixedDt);

    const plan = planSteps(
      this.accumulator,
      frameMs / 1000,
      this.opts.fixedDt,
      this.opts.maxStepsPerFrame,
    );
    this.accumulator = plan.accumulator;

    if (plan.dropped + lost > 0) this.opts.onDropped?.(plan.dropped + lost);
    for (let i = 0; i < plan.steps; i++) {
      this.opts.update(this.opts.fixedDt);
    }
    if (this.minRenderIntervalMs <= 0 || now - this.lastRenderAt >= this.minRenderIntervalMs) {
      this.lastRenderAt = now;
      this.opts.render(plan.alpha);
    } else {
      this.opts.onRenderSkipped?.();
    }
  };
}
